import React, { useRef } from "react";
import { useClickAway, useToggle } from "react-use";

import { useRouter } from "next/router";
import AvatarIcon from "public/icons/account-icon.svg";
import GradeIcon from "public/icons/grade.svg";
import PurchaseIcon from "public/icons/purchase-icon.svg";

import {
  Avatar,
  IconButton,
  MenuItem,
  Theme,
  useMediaQuery,
} from "@mui/material";

import {
  GetLeftInfoResponseDTO,
  GetLeftInfoResponseDTOSuccess,
} from "@/api/left-panel/models";
import { ButtonWithIcon } from "@/components/form/Button/ButtonWithIcon";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { useSendSignOut } from "@/features/forms/sign-in-form/hooks/useSendSignOut.api";
import { theme } from "@/theme/theme";
import { clsxm } from "@/utils/tailwindMerge";

const UnmemoizedProfileSection = ({
  profileData,
}: {
  profileData: GetLeftInfoResponseDTO | null;
}) => {
  const [isMenuOpen, toggleMenu] = useToggle(false);

  const clickAwayElement = useRef<HTMLDivElement>(null);
  useClickAway(clickAwayElement, () => toggleMenu(false), ["click"]);

  const router = useRouter();
  const handleNavigation = (path: string) => {
    router.push(path);
  };

  const { mutate: signOut } = useSendSignOut();

  const { openModal } = useOpenModal();

  const openInformationModal = (str: string) =>
    openModal("INFORMATION_MODAL", {
      message: str,
      statusReg: "success",
    });

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  const menuItemStyles = {
    fontSize: isMobile ? "13px" : "14px",
    minHeight: isMobile ? "27px !important" : "auto",
    transition: "all .3s ease-in-out",
    padding: "0px",

    "&:not(:last-child)": {
      paddingBottom: isMobile ? "0px" : "6px",
    },
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.green[600] + "!important",
    },
  };
  const parsed = GetLeftInfoResponseDTOSuccess.safeParse(profileData);

  return (
    <div className="w-full pt-2 max-w-sm mx-auto">
      <div className="flex justify-around items-center mb-4">
        <div className="flex flex-col items-center text-[11px] font-bold">
          <div className="w-8 h-8 rounded-full flex items-center justify-center">
            {parsed.success && parsed.data.user.url ? (
              <div
                onClickCapture={(e) => {
                  e.stopPropagation();
                  toggleMenu();
                }}
                className="flex flex-col items-center"
              >
                <IconButton
                  aria-label="Profile"
                  disableRipple
                  className="w-12 h-12 rounded-full overflow-hidden border border-grey1-100 bg-opacity-40 "
                >
                  <div className="w-full h-full mt-4">
                    <Avatar
                      src={parsed.data.user.url}
                      alt="avatar"
                      sx={{
                        width: 40,
                        height: 40,
                        "& img": {
                          objectFit: "cover",
                          objectPosition: "top",
                        },
                      }}
                    />
                  </div>
                </IconButton>

                <p className="text-[11px] font-bold cursor-pointer">
                  My Profile
                </p>
              </div>
            ) : (
              <ButtonWithIcon
                onClickCapture={(e) => {
                  e.stopPropagation();
                  toggleMenu();
                }}
                ariaLabel="Profile"
                label="My Profile"
                iconColor="light-grey"
                iconHoverColor="white"
                disabledHover
                Icon={AvatarIcon}
                className="z-[1] transition-all animate-fadeIn bg-grey1-100 bg-opacity-40 hover:bg-grey1-100 hover:bg-opacity-40"
              />
            )}
          </div>
        </div>
        <div className="flex flex-col items-center text-[11px] font-bold">
          <div className="w-8 h-8 rounded-full flex items-center justify-center">
            <ButtonWithIcon
              onClick={() => openInformationModal("Purchase modal here")}
              ariaLabel="Purchase"
              label="Purchase"
              iconColor="light-grey"
              iconHoverColor="white"
              Icon={PurchaseIcon}
              className={`z-[1] w-8 h-8 transition-all animate-fadeIn bg-grey1-100 bg-opacity-40 hover:bg-blue`}
            />
          </div>
        </div>

        <div className="flex flex-col items-center text-[11px] font-bold">
          <div className="w-8 h-8 rounded-full flex items-center justify-center">
            <ButtonWithIcon
              onClick={() => handleNavigation("/favorites")}
              ariaLabel="Favorite"
              label="Favorite"
              iconColor="light-grey"
              iconHoverColor="white"
              Icon={GradeIcon}
              className={`z-[1] w-8 h-8 transition-all animate-fadeIn bg-grey1-100 bg-opacity-40 hover:bg-blue`}
            />
          </div>
        </div>
      </div>
      <div
        ref={clickAwayElement}
        className={clsxm(
          "grid",
          !isMenuOpen ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
          "[transition:_all_0.3s]",
          isMenuOpen ? "mb-2 mt-5" : "my-0",
        )}
      >
        <div
          className={clsxm(
            "overflow-hidden",
            "rounded-lg shadow-[0_4px_16px_rgba(0,_0,_0,_0.1)]",
          )}
        >
          <div className={clsxm(isMobile ? "p-2 px-3" : "p-3 px-4")}>
            <MenuItem
              onClick={() => handleNavigation("/profile")}
              sx={menuItemStyles}
            >
              My Profile
            </MenuItem>
            <MenuItem
              onClick={() => handleNavigation("/black-list")}
              sx={menuItemStyles}
            >
              Black list
            </MenuItem>
            <MenuItem
              onClick={() => handleNavigation("/recharge-history")}
              sx={menuItemStyles}
            >
              Recharge history
            </MenuItem>
            <MenuItem
              onClick={() => handleNavigation("/customer-support")}
              sx={menuItemStyles}
            >
              <span>Customer Support</span>
              {parsed.success && parsed.data.unreadAdminMail ? (
                <span className="ml-1 font-bold">
                  ({parsed.data.unreadAdminMail})
                </span>
              ) : null}
            </MenuItem>

            <MenuItem onClick={() => signOut()} sx={menuItemStyles}>
              Logout
            </MenuItem>
          </div>
        </div>
      </div>

      <div className="flex gap-2 text-xs bg-grey1-50 rounded-full mt-1 p-1 px-3">
        {(() => {
          const activeCount = [
            true,
            parsed.success && parsed.data.balance.bonusEmailRead,
            parsed.success && parsed.data.balance.bonusCoins,
          ].filter(Boolean).length;

          return (
            <>
              {activeCount === 3 ? (
                <div className="flex flex-col w-full text-center">
                  <div className="flex justify-between">
                    <span className="text-grey1-200 ">Balance:</span>
                    <span className="text-grey1-200 ">Bonus letter:</span>
                    <span className="text-grey1-200 ">Bonuses:</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-grey1-900 font-bold w-1/3 ml-[-9px] ">
                      {parsed.success ? parsed.data.balance.coins : "0"}
                    </span>
                    <span className="text-grey1-900 font-bold w-1/3 ">
                      {parsed.success && parsed.data.balance.bonusEmailRead}
                    </span>
                    <span className="text-grey1-900 font-bold w-1/3 mr-[-6px] ">
                      {parsed.success && parsed.data.balance.bonusCoins}
                    </span>
                  </div>
                </div>
              ) : (
                <div
                  className={`flex ${
                    activeCount === 1
                      ? "w-full justify-center"
                      : "justify-between w-full"
                  }`}
                >
                  <div className="flex">
                    <p className="text-grey1-200">
                      Balance:
                      <span className="text-grey1-900 font-bold ml-1">
                        {parsed.success ? parsed.data.balance.coins : "0"}
                      </span>
                    </p>
                  </div>

                  {parsed.success && parsed.data.balance.bonusEmailRead ? (
                    <div className="flex">
                      <p className="text-grey1-200">
                        Bonus letter:
                        <span className="text-grey1-900 font-bold ml-1">
                          {parsed.data.balance.bonusEmailRead}
                        </span>
                      </p>
                    </div>
                  ) : null}

                  {parsed.success && parsed.data.balance.bonusCoins ? (
                    <div className="flex">
                      <p className="text-grey1-200">
                        Bonuses:
                        <span className="text-grey1-900 font-bold ml-1">
                          {parsed.data.balance.bonusCoins}
                        </span>
                      </p>
                    </div>
                  ) : null}
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

export const ProfileSection = React.memo(UnmemoizedProfileSection);
